import { z } from 'zod';
import { BudgetRefSchema } from './budgets';
import { BudgetCheckSchema } from './procurement';
import { BusinessCodeSchema, RecordIdSchema } from './identifiers';
import { MoneySchema, compareMoney, type Money } from './money';
import { BusinessDateSchema, InstantSchema } from './time';

/**
 * General expenses — `FIN-CASH-004` (G-02).
 *
 * An expense is entered by the person who incurred or will pay it, with its papers, a budget line where
 * one applies (the purchase budget check, `BD-49`) and the ledger account it belongs to. It is approved
 * by someone else holding `finance.expense.approve` within their authority limit (`BD-57`), or through
 * the approval engine where a policy governs `finance.expense.approve`. Approval records
 * `expense.approved`; payment is a treasury payment request (BD-59) and the expense counts as paid
 * only when treasury executes it. Nothing is deleted: drafts are cancelled, approved expenses stay.
 */

export const EXPENSE_STATES = [
  'draft',
  'submitted',
  'approved',
  'paymentRequested',
  'paid',
  'rejected',
  'cancelled',
] as const;
export const ExpenseStateSchema = z.enum(EXPENSE_STATES);
export type ExpenseState = z.infer<typeof ExpenseStateSchema>;

export const EXPENSE_PAYEES = ['requester', 'vendor'] as const;

export const ExpenseSchema = z.strictObject({
  expenseId: RecordIdSchema,
  number: z.string(),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  projectId: RecordIdSchema.optional(),
  /** The project phase or work package (FIN-REPORT-005); only with a project. */
  projectPhase: z.string().optional(),
  departmentId: RecordIdSchema.optional(),
  costCenterCode: z.string().optional(),
  category: z.string(),
  description: z.string(),
  incurredOn: BusinessDateSchema,
  amount: MoneySchema,
  expenseAccountId: RecordIdSchema.optional(),
  budget: BudgetRefSchema.optional(),
  budgetCheck: BudgetCheckSchema.optional(),
  payTo: z.enum(EXPENSE_PAYEES),
  vendorId: RecordIdSchema.optional(),
  state: ExpenseStateSchema,
  requestedByAccountId: z.string(),
  approvedByAccountId: z.string().optional(),
  approvalRequestId: z.string().optional(),
  paymentRequestId: z.string().optional(),
  rejection: z
    .strictObject({ reason: z.string(), byAccountId: z.string(), at: InstantSchema })
    .optional(),
  cancelReason: z.string().optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type Expense = z.infer<typeof ExpenseSchema>;

export const CreateExpenseSchema = z
  .strictObject({
    branchId: RecordIdSchema,
    projectId: RecordIdSchema.optional(),
    projectPhase: z.string().trim().min(1).max(80).optional(),
    departmentId: RecordIdSchema.optional(),
    costCenterCode: BusinessCodeSchema.optional(),
    category: z.string().trim().min(2).max(80),
    description: z.string().trim().min(3).max(1000),
    incurredOn: BusinessDateSchema,
    amount: MoneySchema,
    expenseAccountId: RecordIdSchema.optional(),
    budget: BudgetRefSchema.optional(),
    payTo: z.enum(EXPENSE_PAYEES),
    vendorId: RecordIdSchema.optional(),
    idempotencyKey: z.string().trim().min(8).max(200),
  })
  .refine((value) => value.payTo !== 'vendor' || !!value.vendorId, {
    message: 'VENDOR_REQUIRED',
    path: ['vendorId'],
  })
  .refine((value) => !value.projectPhase || !!value.projectId, {
    message: 'PROJECT_REQUIRED',
    path: ['projectPhase'],
  });
export type CreateExpense = z.infer<typeof CreateExpenseSchema>;

export const ExpenseActionSchema = z.strictObject({
  expectedVersion: z.number().int().positive(),
  reason: z.string().trim().min(3).max(500).optional(),
});
export const ExpensePaymentSchema = z.strictObject({
  fromAccountId: RecordIdSchema,
  method: z.enum(['bankTransfer', 'cash', 'cheque']),
  idempotencyKey: z.string().trim().min(8).max(200),
});
export const ExpenseQuerySchema = z.strictObject({
  state: ExpenseStateSchema.optional(),
  mine: z.enum(['true', 'false']).optional(),
  projectId: RecordIdSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().min(1).max(200).optional(),
});
export type ExpenseQuery = z.infer<typeof ExpenseQuerySchema>;

/* ------------------------------------------------- authority limits (BD-57) */

/**
 * The most a role may approve on its own, per currency (`BD-57`). `null`: not configured — direct
 * approvals are not limited, and the screens say so. Once configured, someone whose roles carry no
 * limit in the currency may not approve directly at all.
 */
export const AuthorityLimitsSchema = z
  .strictObject({
    limits: z
      .array(z.strictObject({ roleKey: z.string().min(1).max(64), limit: MoneySchema }))
      .max(100),
  })
  .nullable();
export type AuthorityLimits = z.infer<typeof AuthorityLimitsSchema>;

/**
 * The most the actor may approve directly in a currency (`BD-57`): `undefined` when no limits are
 * configured, `null` when limits exist and none of the actor's roles carries one in that currency.
 */
export function authorityLimitOf(
  limits: AuthorityLimits,
  roleKeys: readonly string[],
  currency: string,
): Money | null | undefined {
  if (!limits) return undefined;
  let best: Money | null = null;
  for (const row of limits.limits) {
    if (!roleKeys.includes(row.roleKey) || row.limit.currency !== currency) continue;
    if (!best || compareMoney(row.limit, best) > 0) best = row.limit;
  }
  return best;
}

export const EXPENSE_APPROVAL_OPERATIONS = { approve: 'finance.expense.approve' } as const;

export const EXPENSE_AUDIT_ACTIONS = {
  created: 'finance.expense.created',
  stateChanged: 'finance.expense.stateChanged',
  paymentRequested: 'finance.expense.paymentRequested',
  paid: 'finance.expense.paid',
} as const;

/* ------------------------------------------- cash advances (FIN-PETTY-001) */

/**
 * A cash advance: money handed to an employee to spend for the company, then accounted for.
 * Requested, approved by someone else (directly within the authority limits, `BD-57`, or through the
 * approval engine where a policy governs `finance.advance.approve`), paid by a treasury payment request
 * (BD-59), then spent: the holder submits what was spent, with papers; someone else reviews each line;
 * approved spend records `cashAdvance.spent`; what is left comes back to a cashbox through treasury.
 * Outstanding = paid − approved spend − returned; it is settled at zero. Spend beyond what was advanced
 * is refused — it is an ordinary expense to reimburse.
 */
export const CASH_ADVANCE_STATES = [
  'requested',
  'approved',
  'paymentRequested',
  'open',
  'settled',
  'rejected',
  'cancelled',
] as const;
export const CashAdvanceStateSchema = z.enum(CASH_ADVANCE_STATES);
export type CashAdvanceState = z.infer<typeof CashAdvanceStateSchema>;

export const SPEND_STATES = ['submitted', 'approved', 'rejected'] as const;

export const CashAdvanceSchema = z.strictObject({
  advanceId: RecordIdSchema,
  number: z.string(),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  projectId: RecordIdSchema.optional(),
  holderAccountId: z.string(),
  purpose: z.string(),
  amount: MoneySchema,
  /** By when it should be accounted for. */
  settleBy: BusinessDateSchema.optional(),
  state: CashAdvanceStateSchema,
  requestedByAccountId: z.string(),
  approvedByAccountId: z.string().optional(),
  approvalRequestId: z.string().optional(),
  paymentRequestId: z.string().optional(),
  paidOn: BusinessDateSchema.optional(),
  spends: z.array(
    z.strictObject({
      spendId: RecordIdSchema,
      incurredOn: BusinessDateSchema,
      category: z.string(),
      description: z.string(),
      amount: MoneySchema,
      expenseAccountId: RecordIdSchema.optional(),
      state: z.enum(SPEND_STATES),
      submittedAt: InstantSchema,
      decidedByAccountId: z.string().optional(),
      decidedAt: InstantSchema.optional(),
      rejectionReason: z.string().optional(),
    }),
  ),
  returns: z.array(
    z.strictObject({
      returnId: RecordIdSchema,
      amount: MoneySchema,
      returnedOn: BusinessDateSchema,
      treasuryAccountId: RecordIdSchema,
      movementId: z.string(),
      byAccountId: z.string(),
      at: InstantSchema,
    }),
  ),
  /**
   * A return not yet received — its cash receipt waits for a `treasury.movement` approval. It reduces
   * nothing: the amount stays outstanding until treasury applies the receipt.
   */
  pendingReturn: z
    .strictObject({
      amount: MoneySchema,
      state: z.enum(['claimed', 'awaitingApproval']),
      returnedOn: BusinessDateSchema.optional(),
      treasuryAccountId: RecordIdSchema.optional(),
      movementId: z.string().optional(),
      approvalRequestId: z.string().optional(),
      byAccountId: z.string().optional(),
      at: InstantSchema,
    })
    .optional(),
  /** Returns whose cash receipt was rejected, withdrawn or expired; the amount stayed outstanding. */
  declinedReturns: z.array(
    z.strictObject({
      amount: MoneySchema,
      returnedOn: BusinessDateSchema,
      treasuryAccountId: RecordIdSchema,
      movementId: z.string(),
      outcome: z.enum(['rejected', 'cancelled', 'expired']),
      reason: z.string(),
      byAccountId: z.string(),
      at: InstantSchema,
    }),
  ),
  totals: z.strictObject({
    paid: MoneySchema,
    approvedSpend: MoneySchema,
    pendingSpend: MoneySchema,
    returned: MoneySchema,
    /** Returns awaiting their cash-receipt approval — not part of `returned`. */
    pendingReturn: MoneySchema,
    outstanding: MoneySchema,
    /** What can still be returned or spent: outstanding − spend under review − return pending. */
    returnable: MoneySchema,
  }),
  /** Open past its settle-by date. */
  overdue: z.boolean(),
  rejection: z
    .strictObject({ reason: z.string(), byAccountId: z.string(), at: InstantSchema })
    .optional(),
  cancelReason: z.string().optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type CashAdvance = z.infer<typeof CashAdvanceSchema>;

export const RequestCashAdvanceSchema = z.strictObject({
  branchId: RecordIdSchema,
  projectId: RecordIdSchema.optional(),
  /** Who holds the money; the requester when omitted. */
  holderAccountId: z.string().min(1).max(200).optional(),
  purpose: z.string().trim().min(3).max(500),
  amount: MoneySchema,
  settleBy: BusinessDateSchema.optional(),
  idempotencyKey: z.string().trim().min(8).max(200),
});
export type RequestCashAdvance = z.infer<typeof RequestCashAdvanceSchema>;

export const SubmitSpendSchema = z.strictObject({
  lines: z
    .array(
      z.strictObject({
        incurredOn: BusinessDateSchema,
        category: z.string().trim().min(2).max(80),
        description: z.string().trim().min(3).max(500),
        amount: MoneySchema,
        expenseAccountId: RecordIdSchema.optional(),
      }),
    )
    .min(1)
    .max(50),
  idempotencyKey: z.string().trim().min(8).max(200),
  expectedVersion: z.number().int().positive(),
});
export type SubmitSpend = z.infer<typeof SubmitSpendSchema>;

export const ReviewSpendSchema = z.strictObject({
  decisions: z
    .array(
      z
        .strictObject({
          spendId: RecordIdSchema,
          decision: z.enum(['approve', 'reject']),
          reason: z.string().trim().min(3).max(500).optional(),
        })
        .refine((value) => value.decision !== 'reject' || !!value.reason, {
          message: 'REASON_REQUIRED',
          path: ['reason'],
        }),
    )
    .min(1)
    .max(50),
  expectedVersion: z.number().int().positive(),
});
export type ReviewSpend = z.infer<typeof ReviewSpendSchema>;

export const ReturnCashSchema = z.strictObject({
  treasuryAccountId: RecordIdSchema,
  amount: MoneySchema,
  returnedOn: BusinessDateSchema,
  idempotencyKey: z.string().trim().min(8).max(200),
  expectedVersion: z.number().int().positive(),
});
export type ReturnCash = z.infer<typeof ReturnCashSchema>;

/** Withdraw a return whose cash receipt still waits for approval; the amount stays outstanding. */
export const WithdrawReturnSchema = z.strictObject({
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});
export type WithdrawReturn = z.infer<typeof WithdrawReturnSchema>;

export const CashAdvanceQuerySchema = z.strictObject({
  state: CashAdvanceStateSchema.optional(),
  mine: z.enum(['true', 'false']).optional(),
  overdue: z.enum(['true', 'false']).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

/** Spend per category in a period: approved advance spend and approved expenses, side by side. */
export const PettySpendReportSchema = z.strictObject({
  from: BusinessDateSchema,
  to: BusinessDateSchema,
  rows: z.array(
    z.strictObject({
      category: z.string(),
      currency: z.string(),
      advanceSpend: MoneySchema,
      expenses: MoneySchema,
      total: MoneySchema,
    }),
  ),
  outstandingAdvances: z.array(
    z.strictObject({
      advanceId: RecordIdSchema,
      number: z.string(),
      holderAccountId: z.string(),
      outstanding: MoneySchema,
      /** A return awaiting its cash-receipt approval; still part of `outstanding`. */
      pendingReturn: MoneySchema,
      overdue: z.boolean(),
    }),
  ),
  generatedAt: InstantSchema,
});
export type PettySpendReport = z.infer<typeof PettySpendReportSchema>;

export const PettySpendReportQuerySchema = z.strictObject({
  from: BusinessDateSchema,
  to: BusinessDateSchema,
});

export const CASH_ADVANCE_APPROVAL_OPERATIONS = { approve: 'finance.advance.approve' } as const;

export const CASH_ADVANCE_AUDIT_ACTIONS = {
  requested: 'finance.advance.requested',
  stateChanged: 'finance.advance.stateChanged',
  paymentRequested: 'finance.advance.paymentRequested',
  paid: 'finance.advance.paid',
  spendSubmitted: 'finance.advance.spendSubmitted',
  spendReviewed: 'finance.advance.spendReviewed',
  returned: 'finance.advance.returned',
  returnPending: 'finance.advance.returnPending',
  returnDeclined: 'finance.advance.returnDeclined',
} as const;

/* ------------------------------------------ recurring expenses (FIN-CASH-006) */

/**
 * A recurring expense template: rent, a subscription, a maintenance contract. Generating creates the
 * period's expense as a **draft** — submitted and approved like any other; nothing is approved for it —
 * and never twice for one period (the template and the period form the expense's key).
 */
export const RECURRENCES = ['monthly', 'quarterly', 'yearly'] as const;

export const ExpenseTemplateSchema = z.strictObject({
  templateId: RecordIdSchema,
  name: z.string(),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  projectId: RecordIdSchema.optional(),
  category: z.string(),
  description: z.string(),
  amount: MoneySchema,
  expenseAccountId: RecordIdSchema.optional(),
  payTo: z.enum(EXPENSE_PAYEES),
  vendorId: RecordIdSchema.optional(),
  recurrence: z.enum(RECURRENCES),
  dayOfMonth: z.number().int().min(1).max(28),
  startsOn: BusinessDateSchema,
  endsOn: BusinessDateSchema.optional(),
  active: z.boolean(),
  /** The periods already generated (yyyy-MM), newest first, at most 24. */
  generatedPeriods: z.array(z.string()),
  /** The next date that will be generated, if any. */
  nextDueOn: BusinessDateSchema.optional(),
  createdByAccountId: z.string(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type ExpenseTemplate = z.infer<typeof ExpenseTemplateSchema>;

export const CreateExpenseTemplateSchema = z
  .strictObject({
    name: z.string().trim().min(2).max(120),
    branchId: RecordIdSchema,
    projectId: RecordIdSchema.optional(),
    category: z.string().trim().min(2).max(80),
    description: z.string().trim().min(3).max(1000),
    amount: MoneySchema,
    expenseAccountId: RecordIdSchema.optional(),
    payTo: z.enum(EXPENSE_PAYEES),
    vendorId: RecordIdSchema.optional(),
    recurrence: z.enum(RECURRENCES),
    dayOfMonth: z.number().int().min(1).max(28),
    startsOn: BusinessDateSchema,
    endsOn: BusinessDateSchema.optional(),
  })
  .refine((value) => value.payTo !== 'vendor' || !!value.vendorId, {
    message: 'VENDOR_REQUIRED',
    path: ['vendorId'],
  })
  .refine((value) => !value.endsOn || value.endsOn >= value.startsOn, {
    message: 'END_BEFORE_START',
    path: ['endsOn'],
  });
export type CreateExpenseTemplate = z.infer<typeof CreateExpenseTemplateSchema>;

export const UpdateExpenseTemplateSchema = z.strictObject({
  active: z.boolean().optional(),
  amount: MoneySchema.optional(),
  endsOn: BusinessDateSchema.nullable().optional(),
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});
export type UpdateExpenseTemplate = z.infer<typeof UpdateExpenseTemplateSchema>;

export const GenerateRecurringSchema = z.strictObject({
  /** Generate every period due up to this date (today when omitted). */
  asOf: BusinessDateSchema.optional(),
});

export const EXPENSE_TEMPLATE_AUDIT_ACTIONS = {
  created: 'finance.expenseTemplate.created',
  updated: 'finance.expenseTemplate.updated',
  generated: 'finance.expenseTemplate.generated',
} as const;

/**
 * The dates a template generates, in order: from its start up to `asOf` (and its end), on its day of
 * the month, every one, three or twelve months. Pure; at most `limit` dates.
 */
export function recurringDates(
  template: {
    recurrence: (typeof RECURRENCES)[number];
    dayOfMonth: number;
    startsOn: string;
    endsOn?: string | undefined;
  },
  asOf: string,
  limit = 36,
): string[] {
  const step = template.recurrence === 'monthly' ? 1 : template.recurrence === 'quarterly' ? 3 : 12;
  const [startYear, startMonth, startDay] = template.startsOn.split('-').map(Number) as [
    number,
    number,
    number,
  ];
  // The first period is the start month when its day is not before the start; otherwise the next one.
  let index = startDay <= template.dayOfMonth ? 0 : step;
  const dates: string[] = [];
  while (dates.length < limit) {
    const months = startYear * 12 + (startMonth - 1) + index;
    const year = Math.floor(months / 12);
    const month = (months % 12) + 1;
    const date = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(template.dayOfMonth).padStart(2, '0')}`;
    if (date > asOf || (template.endsOn && date > template.endsOn)) break;
    dates.push(date);
    index += step;
  }
  return dates;
}
